/**
 * Settlement List — the one net cash movement per billing entity per period.
 *
 * Pilot-minimal (spec §9): list, the three PDFs, record cash, resend the email.
 * Resend only: a statement is issued and first sent by releasing its billing run.
 * Cash is recorded HERE and nowhere else (spec §6): a settled month is immutable.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Paid as CashIcon,
  Replay as ResendIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { settlementApi, selfBillApi, invoiceApi } from '../../services/invoicingApi';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const statusColors = {
  draft: 'default',
  issued: 'info',
  disputed: 'error',
  settled: 'success',
  cancelled: 'default',
};

const directionLabel = (s) => {
  if (s.net_direction === 'handoff_to_retailer') return `HandOff pays ${s.legal_name || 'retailer'}`;
  if (s.net_direction === 'retailer_to_handoff') return `${s.legal_name || 'Retailer'} pays HandOff`;
  return 'Balanced — no payment';
};

const money = (v, currency = 'EUR') =>
  `${currency} ${Number(v || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function openBlob(blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function SettlementList({ focusStatement, onClearFocus, onOpenDocuments }) {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [testMode, setTestMode] = useState(true);

  const [cashDialog, setCashDialog] = useState(null); // the settlement being settled
  const [cashDate, setCashDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [cashReference, setCashReference] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchSettlements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settlementApi.list({ limit: rowsPerPage, offset: page * rowsPerPage, test_mode: testMode });
      setSettlements(result.settlements || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, testMode]);

  useEffect(() => {
    fetchSettlements();
  }, [fetchSettlements]);

  const viewPdf = async (kind, s) => {
    try {
      if (kind === 'invoice') openBlob(await invoiceApi.downloadPdf(s.handoff_invoice_id, { test_mode: testMode }));
      if (kind === 'self_bill') openBlob(await selfBillApi.downloadPdf(s.self_billed_invoice_id, { test_mode: testMode }));
      if (kind === 'statement') openBlob(await settlementApi.downloadStatement(s.id, { test_mode: testMode }));
    } catch (err) {
      setError(`Could not open PDF: ${err.message}`);
    }
  };

  const handleRecordCash = async () => {
    if (!cashDialog) return;
    setBusy(true);
    try {
      const needsCash = Number(cashDialog.cash_amount) > 0;
      await settlementApi.recordCash(cashDialog.id, {
        cash_payment_date: needsCash ? cashDate : undefined,
        cash_payment_reference: needsCash ? cashReference : undefined,
        test_mode: testMode,
      });
      setCashDialog(null);
      setCashReference('');
      fetchSettlements();
    } catch (err) {
      setError(err.body?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async (s) => {
    const first = format(new Date(s.sent_at), 'MMM d, yyyy');
    if (!window.confirm(`Resend ${s.statement_number}? The same documents, first sent ${first}, go out again.`)) return;
    try {
      const result = await settlementApi.resend(s.id, { test_mode: testMode });
      setNotice(`Sent ${s.statement_number} to ${(result.recipients || []).join(', ')} via ${result.transport}`);
      fetchSettlements();
    } catch (err) {
      setError(err.body?.message || err.body?.error || err.message);
    }
  };

  const formatPeriod = (start, end) =>
    start && end ? `${format(new Date(start), 'MMM d, yyyy')} – ${format(new Date(end), 'MMM d, yyyy')}` : '-';

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {focusStatement && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={onClearFocus}>
          Highlighting statement <strong>{focusStatement}</strong>.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Settlements</Typography>
          <FormControlLabel
            control={<Switch checked={testMode} onChange={(e) => setTestMode(e.target.checked)} color="warning" />}
            label={<Chip label={testMode ? 'TEST MODE' : 'PRODUCTION'} color={testMode ? 'warning' : 'error'} size="small" />}
          />
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchSettlements} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Statement</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">HandOff → retailer</TableCell>
              <TableCell align="right">Retailer → HandOff</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent / due</TableCell>
              <TableCell>Documents</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settlements.map((s) => (
              <TableRow key={s.id} hover selected={s.statement_number === focusStatement}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{s.statement_number}</TableCell>
                <TableCell>{s.legal_name}</TableCell>
                <TableCell>{formatPeriod(s.period_start, s.period_end)}</TableCell>
                <TableCell align="right">{money(s.handoff_receivable_total, s.currency)}</TableCell>
                <TableCell align="right">{money(s.handoff_payable_total, s.currency)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">{money(s.cash_amount, s.currency)}</Typography>
                  <Typography variant="caption" color="text.secondary">{directionLabel(s)}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={s.status} color={statusColors[s.status] || 'default'} size="small" />
                    {s.is_overdue && (
                      <Tooltip title={`Due ${s.due_date ? format(new Date(s.due_date), 'MMM d, yyyy') : ''}; no payment recorded`}>
                        <Chip label="overdue" color="error" variant="outlined" size="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">
                    {s.sent_at ? `sent ${format(new Date(s.sent_at), 'MMM d')}` : 'not sent'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.due_date ? `due ${format(new Date(s.due_date), 'MMM d, yyyy')}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  {s.handoff_invoice_id && (
                    <Tooltip title={`Invoice ${s.invoice_number}`}>
                      <IconButton size="small" onClick={() => viewPdf('invoice', s)}><PdfIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  {s.self_billed_invoice_id && (
                    <Tooltip title={`Self-bill ${s.self_bill_number}`}>
                      <IconButton size="small" color="secondary" onClick={() => viewPdf('self_bill', s)}><PdfIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={`Statement ${s.statement_number}`}>
                    <IconButton size="small" color="primary" onClick={() => viewPdf('statement', s)}><PdfIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {onOpenDocuments && (
                    <Tooltip title="Show both documents in Invoices">
                      <Button size="small" onClick={() => onOpenDocuments(s.statement_number)} sx={{ minWidth: 0, ml: 0.5 }}>
                        Documents
                      </Button>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {(s.status === 'issued') && (
                    <Tooltip title="Record cash settlement">
                      <IconButton size="small" color="success" onClick={() => setCashDialog(s)}><CashIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  {s.status !== 'cancelled' && s.sent_at && (
                    <Tooltip title={`Resend the same documents (first sent ${format(new Date(s.sent_at), 'MMM d, yyyy')})`}>
                      <IconButton size="small" onClick={() => handleResend(s)}><ResendIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  {s.status !== 'cancelled' && !s.sent_at && (
                    <Tooltip title="Not issued yet: release its billing run (Billing runs) to issue and send it">
                      <Chip size="small" color="warning" variant="outlined" label="not sent" />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && settlements.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No settlements yet — run a billing run.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      <Dialog open={!!cashDialog} onClose={() => setCashDialog(null)}>
        <DialogTitle>Record cash settlement</DialogTitle>
        <DialogContent>
          {cashDialog && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{cashDialog.statement_number}</strong> — {directionLabel(cashDialog)}:{' '}
                <strong>{money(cashDialog.cash_amount, cashDialog.currency)}</strong>
              </Typography>
              {Number(cashDialog.cash_amount) > 0 ? (
                <>
                  <TextField label="Payment date" type="date" value={cashDate} onChange={(e) => setCashDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} fullWidth sx={{ mb: 2 }} />
                  <TextField label="Bank reference" value={cashReference} onChange={(e) => setCashReference(e.target.value)}
                    placeholder="Transaction reference from the bank statement" fullWidth required />
                </>
              ) : (
                <Alert severity="info">Balanced: no cash moved. Settling closes the month.</Alert>
              )}
              <Alert severity="warning" sx={{ mt: 2 }}>
                A settled month is immutable. Corrections go on next month's documents.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleRecordCash}
            disabled={busy || (cashDialog && Number(cashDialog.cash_amount) > 0 && !cashReference.trim())}
            startIcon={busy ? <CircularProgress size={16} /> : <CashIcon />}>
            Mark settled
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
